import "./bulkExportDrawer.css";

import {
  BULK_EXPORT_STARTED,
  DEFAULT_ERROR_MESSAGE,
  DOWNLOAD_WITHOUT_RESULT,
  LARGE_EXPORT_WARNING,
} from "../constants";
import {
  Badge,
  Button,
  Collapse,
  FormGroup,
  Input,
  Label,
  Spinner,
} from "reactstrap";
import React, { useMemo, useState } from "react";
import {
  requestClientBulkExport,
  requestDownloadSiteFeatures,
} from "../services/backendRequests";

import { Drawer } from "../components/Drawer";
import PropTypes from "prop-types";
import { CfSelect as Select } from "../components/CfSelect";
import { components } from "react-select";
import download from "downloadjs";
import { hasFeaturesRole } from "../users/rolesUtil";
import { useSnackbar } from "notistack";

const FORMATS = ["csv", "geojson"];

// Plot features are the only file kind the backend produces for now
// (see BulkExportsController::FILE_KINDS), so the UI offers no choice and
// always sends it. Bring a picker back with the next kind.
const FILES = ["features"];

// Dates multi-select styled like the CLOV-705 roles picker (`cf-rs` classes):
// a trailing tick on selected options and a compact "N selected" summary in the
// control instead of a pile of chips.
const DateOption = (props) => (
  <components.Option {...props}>
    <span className="cf-rs__optlabel">{props.label}</span>
    {props.isSelected && (
      <i className="fa-solid fa-check cf-rs__tick" aria-hidden="true" />
    )}
  </components.Option>
);

const DatesValueContainer = ({ children, ...props }) => {
  const count = props.getValue().length;
  const input = React.Children.toArray(children).pop();
  return (
    <components.ValueContainer {...props}>
      {count > 0 ? (
        <span className="cf-rs__summary">{count} date(s) selected</span>
      ) : (
        children
      )}
      {count > 0 && input}
    </components.ValueContainer>
  );
};

const BulkExportDrawer = ({
  isOpen,
  onClose,
  contracts,
  trials,
  user,
  // Optional, two callers:
  // - the site page: { contractInfoId, site: {id, display_name}, dates: [],
  //   availableDates: [] }, a single-site section with a date picker;
  // - the map, with contracts too: { site: {id, display_name}, dates: [], availableDates: [],
  //   plotIds: [], filterActive: bool, profileName }, the campaign tree with
  //   the site preselected, the profile choice only for that site alone.
  filterContext = null,
  // Extra exports drawn under the selection (the analytics filtered export).
  children = null,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  // Only campaigns the user has access to ever reach this component (the store
  // is populated from /api/client/contract_infos). Of those, only the ones
  // where they may read features can be exported (the backend 403s the rest).
  const accessibleContracts = useMemo(
    () => (contracts || []).filter((c) => hasFeaturesRole(c.roles)),
    [contracts]
  );

  // A trial exists for each site that actually has exportable data; its
  // `trial_dates` are the dates with data (already filtered to mission dates
  // server-side). We key off this so sites/campaigns with nothing to export are
  // shown but disabled - and we get their dates without any extra request.
  const datesBySite = useMemo(() => {
    const map = {};
    (trials || []).forEach((t) => {
      map[t.id] = [...(t.trial_dates || [])].map(String).sort();
    });
    return map;
  }, [trials]);

  const datesOf = (site) => datesBySite[site.id] || [];
  const siteHasData = (site) => datesOf(site).length > 0;
  const campaignDataSites = (c) => (c.sites || []).filter(siteHasData);

  const mapScope = Boolean(filterContext && contracts);
  // siteSelection: { [siteId]: string[] }. A key present means the site is
  // selected; its value is the chosen dates ([] = every date of the site).
  // Empty object => nothing picked => export everything.
  // From the map, the site shown starts checked on the date shown.
  const [siteSelection, setSiteSelection] = useState(() => {
    if (!mapScope) return {};
    const { site, dates, availableDates } = filterContext;
    return { [site.id]: dates.length < availableDates.length ? dates : [] };
  });
  // A lone campaign (opened from its page) starts expanded to show its sites.
  const [expandedCampaigns, setExpandedCampaigns] = useState(() =>
    accessibleContracts.length === 1
      ? { [accessibleContracts[0].id]: true }
      : {}
  );
  const [expandedSites, setExpandedSites] = useState({});

  const [formats, setFormats] = useState(FORMATS);
  const [mergeDates, setMergeDates] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  // An empty plot list is unresolved, not empty: sent as an include list the
  // backend keeps nothing and the export completes empty.
  const noPlots = Boolean(filterContext) && !filterContext.plotIds?.length;
  // Profile choice: "filtered" exports only the plots shown on the map.
  const [filteredMode, setFilteredMode] = useState(
    noPlots || !filterContext?.filterActive ? "all" : "filtered"
  ); // "filtered" | "all"
  // Dates picked in the single-site section (site page, no tree). Defaults to
  // filterContext.dates; the site's other dates are in the date picker.
  // Read at mount, which is why the call sites mount the drawer only while it
  // is open: a permanently mounted drawer would freeze the very first filter
  // context (no trial yet at page load => Export disabled for good).
  const [filteredDates, setFilteredDates] = useState(
    filterContext?.dates || []
  );

  const selectedSiteCount = Object.keys(siteSelection).length;

  // Dates the profile would be exported on: the map site's row in the tree
  // ([] = all its dates). The profile is a map-only choice.
  const mapSiteDates = mapScope && siteSelection[filterContext.site.id];
  const profileApplies = mapScope && selectedSiteCount === 1 && !!mapSiteDates;
  const profileDates = !profileApplies
    ? []
    : mapSiteDates.length
    ? mapSiteDates
    : filterContext.availableDates;
  // The plot list is read on the map's date: on any other date it may not be
  // the plots the profile would keep there.
  const beyondMapDate = profileDates.some(
    (date) => !filterContext.dates.includes(date)
  );
  // A disabled radio keeps its tick: the guard has to live here too.
  const onlyFiltered =
    filteredMode === "filtered" && !noPlots && profileApplies;

  // Merging dates into one CSV per site only means something when CSV is
  // exported and at least one site has more than one date selected.
  const exportedSelection = Object.entries(siteSelection);
  const multiDate =
    filterContext && !mapScope
      ? filteredDates.length > 1
      : exportedSelection.some(
          ([siteId, dates]) =>
            (dates.length || (datesBySite[siteId] || []).length) > 1
        );
  const mergeAvailable = formats.includes("csv") && multiDate;

  // --- search / filtering ----------------------------------------------------

  const q = query.trim().toLowerCase();
  const campaignLabel = (c) =>
    c.company ? `${c.company.name} - ${c.name}` : c.name;
  const matchText = (text) => (text || "").toLowerCase().includes(q);

  // Sites shown for a campaign given the search query, ordered with the
  // exportable ones (those with data) first, then alphabetically. When the
  // campaign name itself matches, all of its sites are shown.
  const visibleSitesOf = (c) => {
    const sites = c.sites || [];
    const filtered =
      !q || matchText(campaignLabel(c))
        ? sites
        : sites.filter((s) => matchText(s.display_name));
    return [...filtered].sort((a, b) => {
      const dataA = siteHasData(a);
      const dataB = siteHasData(b);
      if (dataA !== dataB) return dataA ? -1 : 1;
      return (a.display_name || "").localeCompare(b.display_name || "");
    });
  };

  const visibleCampaigns = accessibleContracts.filter(
    (c) => !q || matchText(campaignLabel(c)) || visibleSitesOf(c).length > 0
  );

  // True when the search matches one of the campaign's sites - only then do we
  // auto-expand it (a match on the campaign name alone keeps it collapsed).
  const campaignSiteMatches = (c) =>
    q !== "" && (c.sites || []).some((s) => matchText(s.display_name));

  const clearSelection = () => {
    setSiteSelection({});
    setExpandedSites({});
  };

  // --- selection helpers -----------------------------------------------------

  const toggleCampaignExpand = (id) =>
    setExpandedCampaigns((prev) => ({ ...prev, [id]: !prev[id] }));

  const toggleSiteExpand = (site) =>
    setExpandedSites((prev) => ({ ...prev, [site.id]: !prev[site.id] }));

  const toggleSite = (site) => {
    if (!siteHasData(site)) return;
    setSiteSelection((prev) => {
      const next = { ...prev };
      if (next[site.id] !== undefined) delete next[site.id];
      else next[site.id] = [];
      return next;
    });
  };

  // Selects/deselects every data-bearing site of the campaign (empty ones can't
  // be exported, so they are ignored).
  const toggleCampaign = (contract) => {
    const sites = campaignDataSites(contract);
    if (sites.length === 0) return;
    setSiteSelection((prev) => {
      const next = { ...prev };
      const allSelected = sites.every((s) => next[s.id] !== undefined);
      sites.forEach((s) => {
        if (allSelected) delete next[s.id];
        else if (next[s.id] === undefined) next[s.id] = [];
      });
      return next;
    });
  };

  const dateChecked = (site, date) => {
    const selected = siteSelection[site.id];
    if (selected === undefined) return false;
    if (selected.length === 0) return true; // "all dates"
    return selected.includes(date);
  };

  const toggleDate = (site, date) =>
    setSiteSelection((prev) => {
      const selected = prev[site.id];
      const allDates = datesOf(site);
      const next = { ...prev };

      if (selected === undefined) {
        next[site.id] = [date]; // selecting a date selects the site
      } else if (selected.length === 0) {
        next[site.id] = allDates.filter((d) => d !== date); // was "all" → all but this
      } else if (selected.includes(date)) {
        const remaining = selected.filter((d) => d !== date);
        if (remaining.length === 0)
          delete next[site.id]; // none left → deselect site
        else next[site.id] = remaining;
      } else {
        const added = [...selected, date];
        next[site.id] = added.length === allDates.length ? [] : added; // all → "all"
      }
      return next;
    });

  // --- output helpers --------------------------------------------------------

  const toggleFormat = (format) =>
    setFormats((prev) =>
      prev.includes(format)
        ? prev.filter((f) => f !== format)
        : [...prev, format]
    );

  // --- submit ----------------------------------------------------------------

  const buildTargets = () =>
    accessibleContracts.flatMap((c) => {
      const sites = (c.sites || [])
        .filter((s) => siteSelection[s.id] !== undefined)
        .map((s) => ({ site_id: s.id, dates: siteSelection[s.id] }));
      return sites.length ? [{ contract_info_id: c.id, sites }] : [];
    });

  const formatsReady = formats.length > 0;
  const singleSiteMode = Boolean(filterContext) && !mapScope;
  // Nothing checked exports nothing: never a silent export of every campaign.
  const hasTarget = singleSiteMode
    ? filteredDates.length > 0
    : selectedSiteCount > 0;
  const canSubmit = !submitting && formatsReady && hasTarget;

  // What the export covers, one [siteId, dates] row per site, "every date"
  // resolved to the site's dates.
  const exportedRows = singleSiteMode
    ? [[filterContext.site.id, filteredDates]]
    : exportedSelection.map(([siteId, dates]) => [
        siteId,
        dates.length ? dates : datesBySite[siteId] || [],
      ]);
  // One date of one site is downloaded right away rather than queued behind
  // the other exports. The download endpoint takes no plot filter, so a
  // profile export always goes through the bulk.
  const singleDate =
    exportedRows.length === 1 &&
    exportedRows[0][1].length === 1 &&
    !onlyFiltered;
  // Several campaigns always mean several sites: a campaign without data
  // exports nothing.
  const largeExport = exportedRows.length > 1;

  const handleDirectDownload = () => {
    const [[siteId, [date]]] = exportedRows;
    setSubmitting(true);
    setError(null);
    Promise.all(
      formats.map((format) =>
        requestDownloadSiteFeatures(siteId, date, format, user)
      )
    )
      .then((downloads) => {
        downloads.forEach(({ blob, filename }) => download(blob, filename));
        onClose();
      })
      .catch((status) => {
        setError(
          status === 404 ? DOWNLOAD_WITHOUT_RESULT : DEFAULT_ERROR_MESSAGE
        );
        setSubmitting(false);
      });
  };

  const runExport = (targets, filter) => {
    setSubmitting(true);
    setError(null);

    const payload = { targets, formats, files: FILES };
    if (filter) payload.filter = filter;
    if (mergeDates && mergeAvailable) payload.merge_dates = true;

    requestClientBulkExport(payload, user)
      .then(() => {
        enqueueSnackbar(BULK_EXPORT_STARTED, {
          variant: "success",
          anchorOrigin: { vertical: "bottom", horizontal: "right" },
        });
        onClose();
      })
      .catch((err) => {
        // handleResponse rejects with the backend message as a plain string.
        setError(
          (typeof err === "string" ? err : err?.message) ||
            DEFAULT_ERROR_MESSAGE
        );
        setSubmitting(false);
      });
  };

  const plotFilter = () => ({
    plot_ids: filterContext.plotIds,
    plot_mode: "include",
  });

  const handleBulkSubmit = () => {
    const targets = buildTargets();
    runExport(targets, onlyFiltered ? plotFilter() : null);
  };

  const handleFilteredSubmit = () => {
    const { contractInfoId, site } = filterContext;
    const targets = [
      {
        contract_info_id: contractInfoId,
        sites: [{ site_id: site.id, dates: filteredDates }],
      },
    ];
    runExport(targets, null);
  };

  const handleSubmit = () => {
    if (singleDate) return handleDirectDownload();
    return singleSiteMode ? handleFilteredSubmit() : handleBulkSubmit();
  };

  // --- render ----------------------------------------------------------------

  const profileChoice = filterContext?.plotIds && (
    <>
      <FormGroup check>
        <Input
          type="radio"
          name="filtered-mode"
          id="filtered-mode-filtered"
          disabled={noPlots}
          checked={filteredMode === "filtered"}
          onChange={() => setFilteredMode("filtered")}
        />
        <Label
          check
          for="filtered-mode-filtered"
          className={noPlots ? "text-muted" : undefined}
        >
          With profiles
          {filterContext.profileName ? (
            <span className="fw-semibold"> - {filterContext.profileName}</span>
          ) : (
            " (active filter)"
          )}
          <Badge pill className="app-drawer-pill ms-2">
            {filterContext.plotIds.length} plot(s)
          </Badge>
        </Label>
      </FormGroup>
      <FormGroup check>
        <Input
          type="radio"
          name="filtered-mode"
          id="filtered-mode-all"
          checked={filteredMode === "all"}
          onChange={() => setFilteredMode("all")}
        />
        <Label check for="filtered-mode-all">
          Without profiles (all plots)
        </Label>
      </FormGroup>
      {noPlots && (
        <small className="text-muted">
          No plot is resolved for the displayed date, so the profile cannot be
          applied: the export covers every plot of the selected dates.
        </small>
      )}
      {!noPlots && !filterContext.filterActive && !beyondMapDate && (
        <small className="text-muted">
          Every plot of this date is shown on the map - both options export the
          same data.
        </small>
      )}
      {/* The plot list comes from the date shown on the map: a filter on
      a trait value may keep other plots on other dates. */}
      {!noPlots && filteredMode === "filtered" && beyondMapDate && (
        <small className="text-muted d-block">
          The profile is computed on the date shown on the map, then its plots
          are exported for every selected date.
        </small>
      )}
    </>
  );

  // Rows with nothing to export get a cross where the checkbox would be: a
  // disabled checkbox still reads as "selectable later", a cross does not. The
  // slot keeps the checkbox's width so labels stay aligned down the tree.
  const NoDataMark = () => (
    <i
      className="fa-solid fa-xmark app-drawer-tree-nodata"
      aria-hidden="true"
    />
  );

  const renderSite = (site) => {
    const hasData = siteHasData(site);
    const dates = datesOf(site);
    const selected = siteSelection[site.id] !== undefined;
    const expanded = !!expandedSites[site.id];
    const chosenDates = siteSelection[site.id] || [];

    // Sites with no exportable data are shown but greyed out and not selectable.
    if (!hasData)
      return (
        <div
          key={site.id}
          className="app-drawer-tree-row app-drawer-tree-row--disabled"
        >
          <span className="app-drawer-tree-expander" />
          <NoDataMark />
          <span className="app-drawer-tree-label text-muted">
            {site.display_name}
          </span>
          <span className="text-muted small fst-italic">no data</span>
        </div>
      );

    return (
      <div key={site.id}>
        <div
          className="app-drawer-tree-row"
          onClick={() => toggleSiteExpand(site)}
        >
          <span className="app-drawer-tree-expander">
            <i
              className={`fa-solid fa-chevron-${expanded ? "down" : "right"}`}
            />
          </span>
          <Input
            type="checkbox"
            className="mt-0"
            id={`bulk-site-${site.id}`}
            checked={selected}
            onClick={(e) => e.stopPropagation()}
            onChange={() => toggleSite(site)}
          />
          <Label
            for={`bulk-site-${site.id}`}
            className="app-drawer-tree-label"
            onClick={(e) => e.stopPropagation()}
          >
            {site.display_name}
          </Label>
          {selected && (
            <Badge pill className="app-drawer-pill">
              {chosenDates.length > 0
                ? `${chosenDates.length} date(s)`
                : "All dates"}
            </Badge>
          )}
        </div>
        <Collapse isOpen={expanded}>
          <div className="app-drawer-tree-dates">
            {dates.map((date) => (
              <FormGroup check key={date}>
                <Input
                  type="checkbox"
                  id={`bulk-date-${site.id}-${date}`}
                  checked={dateChecked(site, date)}
                  onChange={() => toggleDate(site, date)}
                />
                <Label check for={`bulk-date-${site.id}-${date}`}>
                  {date}
                </Label>
              </FormGroup>
            ))}
            <small className="text-muted">
              {selected && chosenDates.length === 0
                ? "All dates included"
                : selected
                ? "Only the checked dates"
                : "Check a date to include this site"}
            </small>
          </div>
        </Collapse>
      </div>
    );
  };

  const renderCampaign = (contract) => {
    const sites = contract.sites || [];
    const shownSites = visibleSitesOf(contract);
    const dataSites = campaignDataSites(contract);
    const hasData = dataSites.length > 0;
    const selectedCount = dataSites.filter(
      (s) => siteSelection[s.id] !== undefined
    ).length;
    const allSelected = hasData && selectedCount === dataSites.length;
    const someSelected = selectedCount > 0 && !allSelected;
    // Respect the manual expand state, and auto-open only when a site (not just
    // the campaign name) matches the search so the result is visible.
    const expanded =
      !!expandedCampaigns[contract.id] || campaignSiteMatches(contract);

    return (
      <div key={contract.id} className="app-drawer-tree-campaign">
        <div
          className="app-drawer-tree-row app-drawer-tree-campaign-row"
          onClick={() => sites.length > 0 && toggleCampaignExpand(contract.id)}
        >
          <span className="app-drawer-tree-expander">
            {sites.length > 0 && (
              <i
                className={`fa-solid fa-chevron-${expanded ? "down" : "right"}`}
              />
            )}
          </span>
          {hasData ? (
            <Input
              type="checkbox"
              className="mt-0"
              id={`bulk-campaign-${contract.id}`}
              checked={allSelected}
              innerRef={(el) => {
                if (el) el.indeterminate = someSelected;
              }}
              onClick={(e) => e.stopPropagation()}
              onChange={() => toggleCampaign(contract)}
            />
          ) : (
            <NoDataMark />
          )}
          {hasData ? (
            <Label
              for={`bulk-campaign-${contract.id}`}
              className="app-drawer-tree-label fw-semibold"
              onClick={(e) => e.stopPropagation()}
            >
              {campaignLabel(contract)}
            </Label>
          ) : (
            <span className="app-drawer-tree-label fw-semibold text-muted">
              {campaignLabel(contract)}
            </span>
          )}
          <span className="text-muted small">
            {hasData
              ? `${selectedCount > 0 ? `${selectedCount}/` : ""}${
                  dataSites.length
                } site${dataSites.length > 1 ? "s" : ""}`
              : "no data"}
          </span>
        </div>
        <Collapse isOpen={expanded}>
          <div className="app-drawer-tree-sites">
            {shownSites.map(renderSite)}
          </div>
        </Collapse>
      </div>
    );
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      wide
      title="Download results"
      footer={
        <>
          {/* In the footer rather than at the top of the body: the error is the
              answer to the Export click, and the footer never scrolls away.
              The large export notice follows the selection as it changes. */}
          {error ? (
            <span
              role="alert"
              className="text-danger small me-auto d-flex align-items-center gap-2"
            >
              <i
                className="fa-solid fa-circle-exclamation"
                aria-hidden="true"
              />
              {error}
            </span>
          ) : (
            largeExport && (
              <span className="small me-auto d-flex align-items-center gap-2">
                <i className="fa-solid fa-circle-info" aria-hidden="true" />
                {LARGE_EXPORT_WARNING}
              </span>
            )
          )}
          <Button
            color="secondary"
            outline
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            className="text-nowrap"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {submitting ? (
              <Spinner size="sm" />
            ) : singleDate ? (
              "Download"
            ) : (
              "Export"
            )}
          </Button>
        </>
      }
    >
      <div className="bulk-export">
        {/* 1. Output. Kept above the tree, which is free to grow as tall as it
            needs: with it below, reaching the format checkboxes would mean
            scrolling past every campaign. */}
        <section className="app-drawer-section">
          <div className="app-drawer-section-title">Output</div>
          <Label className="fw-semibold mb-1 d-block">Formats</Label>
          {FORMATS.map((format) => (
            <FormGroup check key={format}>
              <Input
                type="checkbox"
                id={`bulk-export-format-${format}`}
                checked={formats.includes(format)}
                onChange={() => toggleFormat(format)}
              />
              <Label check for={`bulk-export-format-${format}`}>
                {format.toUpperCase()}
              </Label>
            </FormGroup>
          ))}
          <FormGroup switch className="mt-2 mb-0">
            <Input
              type="switch"
              id="bulk-export-merge-dates"
              disabled={!mergeAvailable}
              checked={mergeDates && mergeAvailable}
              onChange={() => setMergeDates((prev) => !prev)}
            />
            <Label
              check
              for="bulk-export-merge-dates"
              className={mergeAvailable ? undefined : "text-muted"}
            >
              All dates in one CSV per site
            </Label>
          </FormGroup>
          {!mergeAvailable && (
            <small className="text-muted">
              Needs CSV and a site with more than one selected date.
            </small>
          )}
        </section>

        {/* 2. Selection, single-site mode: the current map site, honouring the
            active filtering profile when "with profiles" is selected. */}
        {filterContext && !mapScope && (
          <section className="app-drawer-section">
            <div className="app-drawer-section-title">Selection</div>
            <p className="mb-2">
              <strong>{filterContext.site.display_name}</strong>
              {/* Single-date sites have no picker, so show the date inline. */}
              {(filterContext.availableDates || []).length <= 1 &&
                filterContext.dates &&
                filterContext.dates.length > 0 && (
                  <span className="text-muted">
                    {" "}
                    - {filterContext.dates.join(", ")}
                  </span>
                )}
            </p>

            {(filterContext.availableDates || []).length > 1 && (
              <FormGroup className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <Label className="fw-semibold mb-0">Dates</Label>
                  <Button
                    color="link"
                    size="sm"
                    className="p-0 text-decoration-none"
                    disabled={
                      filteredDates.length ===
                      filterContext.availableDates.length
                    }
                    onClick={() =>
                      setFilteredDates(filterContext.availableDates)
                    }
                  >
                    All dates ({filterContext.availableDates.length})
                  </Button>
                </div>
                <Select
                  className="cf-rs"
                  classNamePrefix="cf-rs"
                  // The drawer body scrolls, which would clip the menu.
                  menuPosition="fixed"
                  isMulti
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  components={{
                    Option: DateOption,
                    ValueContainer: DatesValueContainer,
                  }}
                  options={filterContext.availableDates.map((d) => ({
                    label: d,
                    value: d,
                  }))}
                  value={filteredDates.map((d) => ({ label: d, value: d }))}
                  onChange={(selected) =>
                    setFilteredDates(selected.map((o) => o.value))
                  }
                  placeholder="Select dates..."
                  noOptionsMessage={() => null}
                  menuPlacement="auto"
                />
              </FormGroup>
            )}
          </section>
        )}

        {/* 2. What to export - campaign → site → date tree (bulk and map) */}
        {(!filterContext || mapScope) && (
          <section className="app-drawer-section">
            <div className="d-flex justify-content-between align-items-center">
              <span className="app-drawer-section-title mb-0">Selection</span>
              {selectedSiteCount > 0 && (
                <Button
                  color="link"
                  size="sm"
                  className="p-0 text-decoration-none"
                  onClick={clearSelection}
                >
                  Clear ({selectedSiteCount})
                </Button>
              )}
            </div>

            <Input
              type="search"
              bsSize="sm"
              className="mt-2 mb-2"
              placeholder="Search a campaign or site…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />

            <p className="text-muted small mb-2">
              {selectedSiteCount === 0
                ? "Nothing selected - check a site, or the campaign to export all its sites."
                : `${selectedSiteCount} site(s) selected.`}
            </p>

            <div className="border rounded app-drawer-tree">
              {visibleCampaigns.length === 0 ? (
                <div className="app-drawer-tree-empty">
                  No campaign or site matches.
                </div>
              ) : (
                visibleCampaigns.map(renderCampaign)
              )}
            </div>
            {profileApplies && <div className="mt-2">{profileChoice}</div>}
            {/* Filter or not: alone, the map site offers the profile choice
                either way, so its absence needs the same explanation. */}
            {mapScope && !profileApplies && (
              <small className="text-muted d-block mt-2">
                Filtering by profile is only available when{" "}
                <strong>{filterContext.site.display_name}</strong> (the site
                shown on the map) is the only site selected. Otherwise every
                plot of the selection is exported.
              </small>
            )}
          </section>
        )}

        {children && (
          <section className="app-drawer-section">{children}</section>
        )}
      </div>
    </Drawer>
  );
};

BulkExportDrawer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  contracts: PropTypes.array,
  trials: PropTypes.array,
  user: PropTypes.object.isRequired,
  filterContext: PropTypes.shape({
    contractInfoId: PropTypes.string,
    site: PropTypes.object,
    dates: PropTypes.array,
    availableDates: PropTypes.array,
    plotIds: PropTypes.array,
    filterActive: PropTypes.bool,
    profileName: PropTypes.string,
  }),
  children: PropTypes.node,
};

export default BulkExportDrawer;
